import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  PriceMatchRequest, ListPriceMatchFilters, CreatePriceMatchInput, ApprovePriceMatchInput,
  listPriceMatchRequests, createPriceMatchRequest, approvePriceMatchRequest,
} from '../services/pricingApi';

interface State {
  items: PriceMatchRequest[];
  loading: boolean;
  saving: boolean;
  /** Error from fetchList — drives the list's AsyncBoundary. */
  error: string | null;
  /** Error from create/approve — kept separate so a failed submit doesn't
   *  make an already-loaded list look broken. */
  saveError: string | null;
  fetchList: (filters?: ListPriceMatchFilters) => Promise<void>;
  create: (input: CreatePriceMatchInput) => Promise<PriceMatchRequest>;
  approve: (id: number, input: ApprovePriceMatchInput) => Promise<PriceMatchRequest>;
}

const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const usePriceMatchStore = create<State>()(
  devtools(
    (set) => ({
      items: [],
      loading: false,
      saving: false,
      error: null,
      saveError: null,
      fetchList: async (filters) => {
        set({ loading: true, error: null });
        try {
          const result = await listPriceMatchRequests(filters);
          set({ items: result.requests, loading: false });
        } catch (err) { set({ loading: false, error: toErr(err) }); }
      },
      create: async (input) => {
        set({ saving: true, saveError: null });
        try {
          const created = await createPriceMatchRequest(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) { set({ saving: false, saveError: toErr(err) }); throw err; }
      },
      approve: async (id, input) => {
        set({ saving: true, saveError: null });
        try {
          const updated = await approvePriceMatchRequest(id, input);
          set((s) => ({ items: s.items.map((r) => (r.id === id ? updated : r)), saving: false }));
          return updated;
        } catch (err) { set({ saving: false, saveError: toErr(err) }); throw err; }
      },
    }),
    { name: 'price-match-store' }
  )
);
